import React from 'react';
import { Paper, Box, Typography, Skeleton, alpha, useTheme } from '@mui/material';

interface KpiTileProps {
  label: string;
  value: string;
  sub?: string;
  icon?: React.ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  loading?: boolean;
}

/**
 * Single KPI tile. Left-accent bar keyed off `tone`; icon renders in a
 * softly tinted square matching the same tone.
 */
const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  sub,
  icon,
  tone = 'neutral',
  loading,
}) => {
  const theme = useTheme();

  const toneColor = (() => {
    switch (tone) {
      case 'primary': return theme.palette.primary.main;
      case 'success': return theme.palette.success.main;
      case 'warning': return theme.palette.warning.main;
      case 'error': return theme.palette.error.main;
      case 'info': return theme.palette.info.main;
      default: return theme.palette.text.secondary;
    }
  })();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: toneColor,
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
          {label}
        </Typography>
        {icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(toneColor, 0.12),
              color: toneColor,
              '& svg': { fontSize: 16 },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      {loading ? (
        <Skeleton variant="text" width={120} height={38} />
      ) : (
        <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
          {value}
        </Typography>
      )}

      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
};

export default KpiTile;
